import React, { useMemo } from 'react'
import { Candle } from '../types'

interface AnalyticsTableProps {
  chartData: Candle[]
  currentPrice?: number
  onClose?: () => void
}

interface AnalyticsData {
  period: string
  change: number
  changePercent: number
}

/**
 * Baby-level explanation:
 * This component calculates and displays percentage changes for different time periods
 * based on the chart data. It shows a table with periods and their corresponding changes.
 * The values are calculated once when the component mounts and don't change with crosshair movements.
 */
const AnalyticsTable: React.FC<AnalyticsTableProps> = ({ chartData, currentPrice, onClose }) => {

  // Calculate analytics data for different time periods - fixed values based on initial chart data
  const analyticsData = useMemo((): AnalyticsData[] => {
    if (!chartData || chartData.length === 0) return []

    // Use the latest candle's close price as the current price (fixed value)
    const latestCandle = chartData[chartData.length - 1]
    const currentClose = latestCandle?.close || 0

    const periods = [
      { label: 'Today', days: 1 },
      { label: '7d', days: 7 },
      { label: '30d', days: 30 },
      { label: '90d', days: 90 },
      { label: '180d', days: 180 },
      { label: '1y', days: 365 }
    ]

    return periods.map(period => {
      const now = Date.now() / 1000 // Current time in seconds
      const targetTime = now - (period.days * 24 * 60 * 60) // Target time in seconds
      
      // Find the closest candle to the target time
      let closestCandle = chartData[0]
      let minTimeDiff = Math.abs((typeof chartData[0].time === 'number' ? chartData[0].time : new Date(chartData[0].time).getTime() / 1000) - targetTime)
      
      for (const candle of chartData) {
        const candleTime = typeof candle.time === 'number' ? candle.time : new Date(candle.time).getTime() / 1000
        const timeDiff = Math.abs(candleTime - targetTime)
        
        if (timeDiff < minTimeDiff) {
          minTimeDiff = timeDiff
          closestCandle = candle
        }
      }

      const pastPrice = closestCandle.close
      const change = currentClose - pastPrice
      const changePercent = pastPrice !== 0 ? (change / pastPrice) * 100 : 0

      return {
        period: period.label,
        change,
        changePercent
      }
    })
  }, [chartData]) // Removed currentPrice dependency to keep values fixed

  return (
    <div
      style={{
        width: '100%',
        backgroundColor: 'transparent',
        borderTop: '1px solid rgba(255, 255, 255, 0.2)',
        padding: '8px 16px',
        fontSize: '11px',
        // color: '#fff',
        fontFamily: 'monospace',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        minHeight: '60px'
      }}
    >
      {/* Analytics Table */}
      <div style={{ flex: 1 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {analyticsData.map((data, index) => (
                <th
                  key={index}
                  style={{
                    padding: '2px 8px',
                    textAlign: 'center',
                    borderBottom: '1px solid #88a1ac33',
                    fontSize: '10px',
                    fontWeight: 'normal'
                  }}
                >
                  {data.period}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              {analyticsData.map((data, index) => {
                const isPositive = data.changePercent >= 0
                return (
                  <td
                    key={index}
                    style={{
                      padding: '2px 8px',
                      textAlign: 'center',
                      color: isPositive ? '#16a085' : '#c0392b',
                      fontSize: '10px',
                      fontWeight: 'bold'
                    }}
                  >
                    {isPositive ? '+' : ''}{data.changePercent.toFixed(2)}%
                  </td>
                )
              })}
            </tr>
          </tbody>
        </table>
      </div>

      {/* Close button */}
      <button
        onClick={onClose}
        style={{
          background: 'transparent',
          border: '1px solid #88a1ac33',
          borderRadius: '4px',
        //   color: '#fff',
          cursor: 'pointer',
          fontSize: '12px',
          lineHeight: '16px',
          padding: '4px 8px',
          marginLeft: '16px',
          minWidth: '24px'
        }}
      >
        ×
      </button>
    </div>
  )
}

export default React.memo(AnalyticsTable)